/**
 * Custom Role Service
 * 
 * Manages custom roles for Enterprise customers
 * Custom roles allow organizations to create fine-grained permissions
 * 
 * @module customRoleService
 */

import { db } from '@/firebase/config';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp,
  Timestamp,
  arrayUnion,
  arrayRemove,
} from 'firebase/firestore';
import { Permission } from './permissionService';
import { isEnterprisePlan } from '@/utils/enterpriseSecurity';

// ============================================================================
// Types
// ============================================================================

export interface CustomRole {
  id: string;
  name: string;
  description: string;
  organizationId: string;
  permissions: Permission[];
  createdBy: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  isActive: boolean;
}

export interface CreateCustomRoleInput {
  name: string;
  description: string;
  organizationId: string;
  permissions: Permission[];
  createdBy: string;
}

export interface UpdateCustomRoleInput {
  name?: string;
  description?: string;
  permissions?: Permission[];
  isActive?: boolean;
}

// ============================================================================
// Custom Role CRUD Operations
// ============================================================================

/**
 * Create a new custom role
 * Only Enterprise customers can create custom roles
 * Only organization admins can create roles
 */
export async function createCustomRole(
  input: CreateCustomRoleInput
): Promise<{ success: boolean; roleId?: string; error?: string }> {
  try {
    // Check if organization has Enterprise plan
    const isEnterprise = await isEnterprisePlan(input.organizationId);
    if (!isEnterprise) {
      return {
        success: false,
        error: 'Custom roles are only available for Enterprise customers',
      };
    }

    // Validate input
    if (!input.name || !input.description || !input.organizationId || !input.permissions) {
      return {
        success: false,
        error: 'Missing required fields',
      };
    }

    // Create custom role document
    const roleData = {
      name: input.name.trim(),
      description: input.description.trim(),
      organizationId: input.organizationId,
      permissions: input.permissions,
      createdBy: input.createdBy,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      isActive: true,
    };

    const roleRef = await addDoc(collection(db, 'custom_roles'), roleData);
    
    return {
      success: true,
      roleId: roleRef.id,
    };
  } catch (error: any) {
    console.error('Error creating custom role:', error);
    return {
      success: false,
      error: error.message || 'Failed to create custom role',
    };
  }
}

/**
 * Get a custom role by ID
 */
export async function getCustomRole(
  roleId: string
): Promise<{ success: boolean; role?: CustomRole; error?: string }> {
  try {
    const roleDoc = await getDoc(doc(db, 'custom_roles', roleId));
    
    if (!roleDoc.exists()) {
      return {
        success: false,
        error: 'Custom role not found',
      };
    }

    const roleData = roleDoc.data();
    const role: CustomRole = {
      id: roleDoc.id,
      ...roleData,
    } as CustomRole;

    return {
      success: true,
      role,
    };
  } catch (error: any) {
    console.error('Error getting custom role:', error);
    return {
      success: false,
      error: error.message || 'Failed to get custom role',
    };
  }
}

/**
 * Get all custom roles for an organization
 */
export async function getCustomRolesByOrganization(
  organizationId: string
): Promise<{ success: boolean; roles?: CustomRole[]; error?: string }> {
  try {
    const q = query(
      collection(db, 'custom_roles'),
      where('organizationId', '==', organizationId)
    );

    const querySnapshot = await getDocs(q);
    const roles: CustomRole[] = [];

    querySnapshot.forEach((doc) => {
      roles.push({
        id: doc.id,
        ...doc.data(),
      } as CustomRole);
    });

    return {
      success: true,
      roles,
    };
  } catch (error: any) {
    console.error('Error getting custom roles:', error);
    return {
      success: false,
      error: error.message || 'Failed to get custom roles',
    };
  }
}

/**
 * Update a custom role
 * Only organization admins can update roles
 */
export async function updateCustomRole(
  roleId: string,
  input: UpdateCustomRoleInput,
  organizationId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    // Verify role belongs to organization
    const roleResult = await getCustomRole(roleId);
    if (!roleResult.success || !roleResult.role) {
      return {
        success: false,
        error: 'Custom role not found',
      };
    }

    if (roleResult.role.organizationId !== organizationId) {
      return {
        success: false,
        error: 'Custom role does not belong to this organization',
      };
    }

    // Build update data
    const updateData: any = {
      updatedAt: serverTimestamp(),
    };

    if (input.name !== undefined) {
      updateData.name = input.name.trim();
    }
    if (input.description !== undefined) {
      updateData.description = input.description.trim();
    }
    if (input.permissions !== undefined) {
      updateData.permissions = input.permissions;
    }
    if (input.isActive !== undefined) {
      updateData.isActive = input.isActive;
    }

    await updateDoc(doc(db, 'custom_roles', roleId), updateData);

    return {
      success: true,
    };
  } catch (error: any) {
    console.error('Error updating custom role:', error);
    return {
      success: false,
      error: error.message || 'Failed to update custom role',
    };
  }
}

/**
 * Delete a custom role
 * Only organization admins can delete roles
 * Note: This does not remove the role from users who have it assigned
 */
export async function deleteCustomRole(
  roleId: string,
  organizationId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    // Verify role belongs to organization
    const roleResult = await getCustomRole(roleId);
    if (!roleResult.success || !roleResult.role) {
      return {
        success: false,
        error: 'Custom role not found',
      };
    }

    if (roleResult.role.organizationId !== organizationId) {
      return {
        success: false,
        error: 'Custom role does not belong to this organization',
      };
    }

    // Instead of deleting, mark as inactive
    // This preserves the role for users who already have it assigned
    await updateDoc(doc(db, 'custom_roles', roleId), {
      isActive: false,
      updatedAt: serverTimestamp(),
    });

    return {
      success: true,
    };
  } catch (error: any) {
    console.error('Error deleting custom role:', error);
    return {
      success: false,
      error: error.message || 'Failed to delete custom role',
    };
  }
}

// ============================================================================
// User Role Assignment
// ============================================================================

/**
 * Assign a custom role to a user
 * Only organization admins can assign roles
 */
export async function assignCustomRoleToUser(
  userId: string,
  roleId: string,
  organizationId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    // Verify role exists and belongs to organization
    const roleResult = await getCustomRole(roleId);
    if (!roleResult.success || !roleResult.role) {
      return {
        success: false,
        error: 'Custom role not found',
      };
    }

    if (roleResult.role.organizationId !== organizationId) {
      return {
        success: false,
        error: 'Custom role does not belong to this organization',
      };
    }

    if (!roleResult.role.isActive) {
      return {
        success: false,
        error: 'Custom role is not active',
      };
    }

    // Add role ID to user's customRoleIds array
    const userRef = doc(db, 'users', userId);
    const customRoleIdsPath = `organizationRoles.${organizationId}.customRoleIds`;

    await updateDoc(userRef, {
      [customRoleIdsPath]: arrayUnion(roleId),
      updatedAt: serverTimestamp(),
    });

    // Clear permission cache for this user
    const { clearPermissionCache } = await import('./permissionService');
    clearPermissionCache(userId, organizationId);

    return {
      success: true,
    };
  } catch (error: any) {
    console.error('Error assigning custom role to user:', error);
    return {
      success: false,
      error: error.message || 'Failed to assign custom role to user',
    };
  }
}

/**
 * Remove a custom role from a user
 * Only organization admins can remove roles
 */
export async function removeCustomRoleFromUser(
  userId: string,
  roleId: string,
  organizationId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    // Remove role ID from user's customRoleIds array
    const userRef = doc(db, 'users', userId);
    const customRoleIdsPath = `organizationRoles.${organizationId}.customRoleIds`;

    await updateDoc(userRef, {
      [customRoleIdsPath]: arrayRemove(roleId),
      updatedAt: serverTimestamp(),
    });

    // Clear permission cache for this user
    const { clearPermissionCache } = await import('./permissionService');
    clearPermissionCache(userId, organizationId);

    return {
      success: true,
    };
  } catch (error: any) {
    console.error('Error removing custom role from user:', error);
    return {
      success: false,
      error: error.message || 'Failed to remove custom role from user',
    };
  }
}

/**
 * Get all users with a specific custom role
 */
export async function getUsersWithCustomRole(
  roleId: string,
  organizationId: string
): Promise<{ success: boolean; userIds?: string[]; error?: string }> {
  try {
    // Verify role belongs to organization
    const roleResult = await getCustomRole(roleId);
    if (!roleResult.success || !roleResult.role) {
      return {
        success: false,
        error: 'Custom role not found',
      };
    }

    if (roleResult.role.organizationId !== organizationId) {
      return {
        success: false,
        error: 'Custom role does not belong to this organization',
      };
    }

    // Query users who have this role
    // Note: Firestore doesn't support querying nested arrays directly
    // We'll need to fetch all users in the organization and filter client-side
    // Or use a Cloud Function for this query
    
    // For now, return empty array - this should be implemented via Cloud Function
    // for better performance with large user bases
    return {
      success: true,
      userIds: [],
    };
  } catch (error: any) {
    console.error('Error getting users with custom role:', error);
    return {
      success: false,
      error: error.message || 'Failed to get users with custom role',
    };
  }
}

// ============================================================================
// Export Service
// ============================================================================

export const customRoleService = {
  createCustomRole,
  getCustomRole,
  getCustomRolesByOrganization,
  updateCustomRole,
  deleteCustomRole,
  assignCustomRoleToUser,
  removeCustomRoleFromUser,
  getUsersWithCustomRole,
};

